import * as React from 'react';
import {
  Create,
  useRedirect,
  useDataProvider,
  useNotify,
} from 'react-admin';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
} from '@mui/material';
import { MemberRole } from '../../enums/MemberRole.ts';
// import { Location } from '../../enums/MemberLocation.ts';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { TextField as MuiTextField, MenuItem, Button as MuiButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MemberService } from "../../services/member/memberService.ts";
import { VehicleType } from '../../enums/VehicleType.ts';
import AgencyService from '../../services/agency/agencyService.ts';
import { LocationService } from '../../services/location/locationService.ts';
import * as Sentry from '@sentry/react';

const MEMBER_CHOICES = Object.entries(MemberRole).map(([id, name]) => ({
  id,
  name,
}));

const VEHICLE_TYPE_CHOICES = Object.entries(VehicleType).map(([id, name]) => ({
  id,
  name,
}));

// const LOCATION_CHOICES = Object.entries(Location).map(([id, name]) => ({
//   id,
//   name,
// }));

const createContainerSx = {
  '& .MuiCardContent-root': { px: 4, py: 0 },
  '& .RaCreate-main': { mx: 4, my: 0 },
  '& .MuiTableBody-root tr:last-of-type td': { borderBottom: 'none' },
};

const headerTitleSx = {
  paddingLeft: 'var(--space-24)',
  fontWeight: 700,
  fontSize: 'var(--text-xxl)',
};

const tableShellSx = {
  border: '1px solid var(--color-secondary)',
  borderRadius: 'var(--radius-6)',
  overflow: 'hidden',
  width: '100%',
};

const headCellSx = {
  paddingLeft: 'var(--space-24)',
  py: 'var(--space-16)',
  fontSize: 'var(--text-xs)',
  fontWeight: 700,
};

const bodyCellSx = {
  py: 'var(--space-24)',
  paddingLeft: 'var(--space-24)',
  fontSize: 'var(--text-xs)',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    border: '1px solid var(--color-secondary)',
    borderRadius: 'var(--radius-6)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
};

const initialValues = {
  role: '',
  // locationId: '',
  agency: '',
  vehicleType: '',
  name: '',
  surname: '',
  email: '',
  phone: '',
  location: '',
};

const validationSchema = Yup.object({
  role: Yup.string().required('Required'),
  // locationId: Yup.string().required('Required'),
  agency: Yup.string(),
  vehicleType: Yup.string(),
  name: Yup.string().required('Required'),
  surname: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  phone: Yup.string()
    .required('Phone number is required')
    .matches(/^\+[0-9]{2,4}[0-9]{5,12}$/, 'Invalid phone number format'),
  location: Yup.string(),
});

export const MembersTableCreate = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const { t } = useTranslation();
  const memberService = new MemberService();
  const agencyService = new AgencyService();
  const locationService = new LocationService();
  const [isCourier, setIsCourier] = React.useState(false);
  const [isVendor, setIsVendor] = React.useState(false);
  const [agencies, setAgencies] = React.useState<any[]>([]);
  const [locations, setLocations] = React.useState<any[]>([]);

  React.useEffect(() => {
    let isActive = true;
    (async () => {
      try {
        const res = await agencyService.getAgencies();
        if (isActive) setAgencies(res);

        const locations = await locationService.getLocations();
        if (isActive) setLocations(locations);
        console.log(res);
      } catch (error) {
        notify(error.response.data.error, { type: 'error' });
        console.error(error.response.data.error);
        Sentry.captureException(error);
      }
    })();
    return () => { isActive = false; };
  }, []);

  return (
    <Create
      title="Members"
      actions={false}
      sx={createContainerSx}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values: any, { setSubmitting }: any) => {
          try {
            if (isCourier) {
              const payload = {
                agencyId: values.agency,
                vehicleType: values.vehicleType,
                userRecordRequest: {
                  email: values.email,
                  phoneNumber: values.phone ?? '',
                  name: [values.name, values.surname].join(' ').trim(),
                }
              };
              console.log(payload);
              await memberService.createCourier(payload);
            }
            else if (isVendor) {
              const payload = {
                role: values.role,
                locationId: values.location,
                userRecordRequest: {
                  email: values.email,
                  phoneNumber: values.phone ?? '',
                  name: [values.name, values.surname].join(' ').trim(),
                }
              };
              console.log("payload: "+JSON.stringify(payload));
              await memberService.createVendor(payload);
            }
            else {
              const payload = {
                userRecordRequest: {
                  email: values.email,
                  phoneNumber: values.phone ?? '',
                  name: [values.name, values.surname].join(' ').trim(),
                },
                role: values.role
              };
              await memberService.createMember(payload);
            }
            notify(t('common.saved'), { type: 'success' });
            redirect('list', 'members');
          } catch (error: any) {
            notify(error.response.data.error, { type: 'error' });
            console.error(error.response.data.error);
            Sentry.captureException(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
          <Form>
            <Box
              sx={{
                paddingTop: 'var(--space-72)',
                paddingBottom: 'var(--space-32)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h5" sx={headerTitleSx}>
                Members
              </Typography>
            </Box>

            <Box sx={tableShellSx}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>{t('members.columns.role')}</TableCell>
                    {/* <TableCell sx={headCellSx}>{t('members.columns.location')}</TableCell> */}
                    {isCourier && <>
                      <TableCell sx={headCellSx}>{t('members.columns.agency')}</TableCell>
                      <TableCell sx={headCellSx}>{t('members.columns.typeOfVehicle')}</TableCell>
                    </>
                    }
                    {isVendor &&
                      <TableCell sx={headCellSx}>{t('members.columns.location')}</TableCell>
                    }
                    <TableCell sx={headCellSx}>{t('members.columns.name')}</TableCell>
                    <TableCell sx={headCellSx}>{t('members.columns.surname')}</TableCell>
                    <TableCell sx={headCellSx}>{t('members.columns.email')}</TableCell>
                    <TableCell sx={headCellSx}>{t('members.columns.phone')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={bodyCellSx}>
                      <MuiTextField
                        select
                        name="role"
                        label=""
                        sx={{ ...inputSx, width: '150px' }}
                        size="small"
                        fullWidth
                        value={values.role}
                        onChange={(e) => {
                          const role = e.target.value;
                          setFieldValue('role', role);
                          if (role === "COURIER") {
                            setIsCourier(true);
                            setIsVendor(false);
                          }
                          else if (role === "VENDOR") {
                            setIsVendor(true);
                            setIsCourier(false);
                          }
                          else {
                            setIsVendor(false);
                            setIsCourier(false);
                          }
                        }}
                        onBlur={handleBlur}
                        helperText={touched.role ? (errors.role || ' ') : ' '}
                      >
                        {MEMBER_CHOICES.map(c => (
                          <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                      </MuiTextField>
                    </TableCell>
                    {/* <TableCell sx={bodyCellSx}>
                      <MuiTextField
                        select
                        name="locationId"
                        label=""
                        sx={inputSx}
                        size="small"
                        fullWidth
                        value={values.locationId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Location"
                        helperText={false}
                      >
                        {LOCATION_CHOICES.map(c => (
                          <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                      </MuiTextField>
                    </TableCell> */}
                    {isCourier && <>
                      <TableCell sx={bodyCellSx}>
                        <MuiTextField
                          select
                          name="agency"
                          label=""
                          sx={{ ...inputSx, width: '150px' }}
                          size="small"
                          fullWidth
                          value={values.agency}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={touched.agency ? (errors.agency || ' ') : ' '}
                        >
                          {agencies.map((agency: { id: number; agencyName: string }) => (
                            <MenuItem key={agency.id} value={agency.id}>
                              {agency.agencyName}
                            </MenuItem>
                          ))}
                        </MuiTextField>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <MuiTextField
                          select
                          name="vehicleType"
                          label=""
                          sx={inputSx}
                          size="small"
                          fullWidth
                          placeholder={t('members.columns.typeOfVehicle')}
                          value={values.vehicleType}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name ? (errors.name || ' ') : ' '}
                        >
                          {VEHICLE_TYPE_CHOICES.map(c => (
                            <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                          ))}
                        </MuiTextField>
                      </TableCell>
                    </>
                    }
                    {isVendor && <>
                      <TableCell sx={bodyCellSx}>
                        <MuiTextField
                          select
                          name="location"
                          label=""
                          sx={{ ...inputSx, width: '150px' }}
                          size="small"
                          fullWidth
                          value={values.location}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={touched.location ? (errors.location || ' ') : ' '}
                        >
                          {locations.map((location: { id: number; name: string }) => (
                            <MenuItem key={location.id} value={location.id}>
                              {location.name}
                            </MenuItem>
                          ))}
                        </MuiTextField>
                      </TableCell>
                    </>
                    }
                    <TableCell sx={bodyCellSx}>
                      <MuiTextField
                        name="name"
                        label=""
                        sx={inputSx}
                        size="small"
                        fullWidth
                        placeholder={t('members.columns.name')}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name ? (errors.name || ' ') : ' '}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <MuiTextField
                        name="surname"
                        label=""
                        sx={inputSx}
                        size="small"
                        fullWidth
                        placeholder={t('members.columns.surname')}
                        value={values.surname}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.surname && Boolean(errors.surname)}
                        helperText={touched.surname ? (errors.surname || ' ') : ' '}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <MuiTextField
                        name="email"
                        type="email"
                        label=""
                        sx={inputSx}
                        size="small"
                        fullWidth
                        placeholder={t('members.columns.email')}
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email ? (errors.email || ' ') : ' '}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <MuiTextField
                        name="phone"
                        label=""
                        sx={inputSx}
                        size="small"
                        fullWidth
                        placeholder={t('members.columns.phone')}
                        value={values.phone}
                        onChange={handleChange}
                        onKeyDown={(e) => {
                          if (e.key === ' ') {
                            e.preventDefault();
                          }
                        }}
                        onBlur={handleBlur}
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone ? (errors.phone || ' ') : ' '}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 6,
                pr: 'var(--space-24)',
              }}
            >
              <Button
                type="button"
                variant="outlined"
                onClick={() => redirect('list', 'members')}
                sx={{ borderRadius: 0, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
              >
                {t('common.cancel')}
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                sx={{
                  borderRadius: 0,
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text-light)',
                  boxShadow: 'none',
                  transition: 'background-color 200ms ease, box-shadow 200ms ease',
                  '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                }}
              >
                {t('common.add')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Create>
  )
};
